import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getIncident, downloadIncidentPdf } from "../../api/incidents";
import { getLang } from "../../store/uiStore";
import { getUser } from "../../store/authStore";
import MedicalErrorForm from "./forms/MedicalErrorForm";
import AdverseEventForm from "./forms/AdverseEventForm";
import InfectionForm from "./forms/InfectionForm";
import "./IncidentDetails.css";

export default function IncidentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const lang = getLang();
  const user = getUser();
  const canDownloadPdf = ["clinic_inspector", "regional_inspector", "central", "admin"].includes(user?.role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getIncident(id);
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  if (loading) {
    return (
      <main className="nizom-incident-details-page">
        <div className="nizom-incident-details-container">
          <section className="nizom-incident-details-state">
            <div className="nizom-details-spinner" aria-hidden="true" />
            <span>
              {lang === "ru" ? "Загрузка..." : "Боргирӣ..."}
            </span>
          </section>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="nizom-incident-details-page">
        <div className="nizom-incident-details-container">
          <section className="nizom-incident-details-state error">
            <div className="nizom-details-state-icon" aria-hidden="true">
              !
            </div>
            <span>
              {lang === "ru" ? "Не найдено" : "Ёфт нашуд"}
            </span>
          </section>
        </div>
      </main>
    );
  }

  const isReadonly = data.status === "sent";

  const TYPE_LABELS = {
    medical_error:
      lang === "ru" ? "Медицинская ошибка" : "Хатои тиббӣ",
    infection:
      lang === "ru"
        ? "Опасное инфекционное заболевание"
        : "Бемории хавфноки сироятӣ",
    adverse_event:
      lang === "ru" ? "Нежелательное явление" : "Ҳодисаи номатлуб",
  };

  const STATUS_LABELS = {
    draft: lang === "ru" ? "Черновик" : "Лоиҳа",
    sent: lang === "ru" ? "Отправлена" : "Фиристода шуд",
  };

  const tr = {
    title:
      lang === "ru"
        ? "Детали уведомления"
        : "Тафсилоти огоҳинома",
    subtitle:
      lang === "ru"
        ? "Просмотр и работа с экстренным извещением."
        : "Тамошо ва кор бо хабарномаи фаврӣ.",
    back: lang === "ru" ? "К списку извещений" : "Ба рӯйхати хабарномаҳо",
    downloadPdf: lang === "ru" ? "Скачать PDF" : "Боргирии PDF",
    date: lang === "ru" ? "Дата" : "Сана",
    clinic: lang === "ru" ? "Клиника" : "Муассиса",
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleString(
      lang === "ru" ? "ru-RU" : "tg-TJ"
    );
  };

  const handleDownloadPdf = async () => {
    if (!data?.id || pdfLoading) return;

    setPdfLoading(true);

    try {
      const response = await downloadIncidentPdf(data.id);
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/pdf" });

      if (blob.size === 0) {
        throw new Error("Сервер вернул пустой PDF");
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = `emergency-notification-${data.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Даём браузеру время начать скачивание перед освобождением URL.
      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (error) {
      console.error("Unable to download incident PDF", error);
      alert(
        lang === "ru"
          ? "Не удалось скачать PDF. Проверьте доступ и попробуйте ещё раз."
          : "PDF-ро боргирӣ карда нашуд. Дастрасиро санҷида, дубора кӯшиш кунед."
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const typeClass = (type) => {
    if (type === "medical_error") return "medical-error";
    if (type === "infection") return "infection";
    if (type === "adverse_event") return "adverse-event";
    return "default";
  };

  return (
    <main className="nizom-incident-details-page">
      <div className="nizom-incident-details-container">
        <header className="nizom-incident-details-header">
          <div className="nizom-incident-details-title-row">
            <span className="nizom-incident-details-title-accent" />
            <div>
              <h1>{tr.title}</h1>
              <p>{tr.subtitle}</p>
            </div>
          </div>

          <div className="nizom-incident-details-actions">
            {canDownloadPdf && data.type === "infection" && data.status === "sent" && (
              <button
                type="button"
                className="nizom-incident-details-download"
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
              >
                {pdfLoading ? "…" : "↓"} {tr.downloadPdf}
              </button>
            )}
            <button
              type="button"
              className="nizom-incident-details-back"
              onClick={() => navigate("/incidents")}
            >
              <span aria-hidden="true">‹</span>
              {tr.back}
            </button>
          </div>
        </header>

        <section className="nizom-incident-summary">
          <div className="nizom-incident-summary-main">
            <span className={`nizom-incident-type ${typeClass(data.type)}`}>
              <span className="nizom-incident-type-dot" />
              {TYPE_LABELS[data.type] || data.type}
            </span>

            <div className="nizom-incident-summary-meta">
              <span>
                {tr.date}: {formatDate(data.created_at || data.createdAt)}
              </span>

              {(data.clinic_name || data.clinic) && (
                <span>
                  {tr.clinic}: {data.clinic_name || data.clinic}
                </span>
              )}
            </div>
          </div>

          <span
            className={`nizom-incident-status ${
              data.status === "draft" ? "draft" : "sent"
            }`}
          >
            {STATUS_LABELS[data.status] || data.status}
          </span>
        </section>

        <section className="nizom-incident-details-form-card">
          {data.type === "medical_error" && (
            <MedicalErrorForm
              initialData={data}
              isReadonly={isReadonly}
              isEdit={!isReadonly}
            />
          )}

          {data.type === "infection" && (
            <InfectionForm
              initialData={data}
              isReadonly={isReadonly}
              isEdit={!isReadonly}
            />
          )}

          {data.type === "adverse_event" && (
            <AdverseEventForm
              initialData={data}
              isReadonly={data.status === "sent"}
              isEdit={data.status !== "sent"}
            />
          )}
        </section>
      </div>
    </main>
  );
}
